import { useState, useEffect } from 'react';
import { fetchRecipients, updateRecipientSelection, bulkUpdateRecipientSelection, updateRecipientNote } from '../../api/relances.api';

const STATUS_BADGES = {
  PENDING: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-600 dark:text-gray-300', label: 'En attente' },
  CLAIMED: { bg: 'bg-slate-100 dark:bg-slate-800/30', text: 'text-slate-800 dark:text-slate-400', label: 'Claimé' },
  RETRY: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', label: 'Retry' },
  SKIPPED: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-300', label: 'Ignoré' },
  SENDING: { bg: 'bg-slate-100 dark:bg-slate-800/30', text: 'text-slate-800 dark:text-slate-400', label: 'Envoi...' },
  SENT: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', label: 'Envoyé' },
  DELIVERED: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', label: 'Délivré' },
  FAILED: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', label: 'Échec' },
  BOUNCED: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', label: 'Rejeté' },
  OPTED_OUT: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', label: 'Opt-out' }
};

export default function RecipientTable({ campaignId, campaignStatus }) {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    channel: '',
    search: ''
  });
  const [pagination, setPagination] = useState({ page: 1, total: 0, limit: 50 });
  const [expandedId, setExpandedId] = useState(null);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [notes, setNotes] = useState({});
  const [savingNote, setSavingNote] = useState({});

  const selectionEnabled = campaignStatus === 'READY';

  useEffect(() => {
    loadRecipients();
  }, [campaignId, filters, pagination.page]);

  const loadRecipients = async () => {
    setLoading(true);
    try {
      const result = await fetchRecipients(campaignId, {
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      });
      setRecipients(result.recipients || []);
      setPagination(p => ({ ...p, total: result.total || 0 }));
    } catch (err) {
      console.error('Error loading recipients:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRecipient = async (recipientId, isSelected) => {
    try {
      await updateRecipientSelection(recipientId, isSelected);
      setRecipients(prev => prev.map(r => (
        r.id === recipientId ? { ...r, is_selected: isSelected } : r
      )));
    } catch (err) {
      console.error('Error updating selection:', err);
      alert(err?.message || 'Impossible de mettre à jour la sélection');
    }
  };

  const handleSaveNote = async (recipientId) => {
    const note = notes[recipientId] ?? '';
    setSavingNote(s => ({ ...s, [recipientId]: true }));
    try {
      await updateRecipientNote(recipientId, note || null);
      setRecipients(prev => prev.map(r => r.id === recipientId ? { ...r, internal_note: note || null } : r));
    } catch {
      alert('Erreur lors de la sauvegarde de la note');
    } finally {
      setSavingNote(s => ({ ...s, [recipientId]: false }));
    }
  };

  const handleToggleAll = async (isSelected) => {
    if (!selectionEnabled || recipients.length === 0) return;
    setBulkUpdating(true);
    try {
      const ids = recipients.map(r => r.id);
      await bulkUpdateRecipientSelection(campaignId, ids, isSelected);
      setRecipients(prev => prev.map(r => ({ ...r, is_selected: isSelected })));
    } catch (err) {
      console.error('Error bulk updating selection:', err);
      alert(err?.message || 'Impossible de mettre à jour la sélection');
    } finally {
      setBulkUpdating(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Filters */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-3">
        <input
          type="text"
          placeholder="Rechercher..."
          value={filters.search}
          onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
          className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm"
        />
        <select
          value={filters.status}
          onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
          className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm"
        >
          <option value="">Tous statuts</option>
          {Object.entries(STATUS_BADGES).map(([key, val]) => (
            <option key={key} value={key}>{val.label}</option>
          ))}
        </select>
        <select
          value={filters.channel}
          onChange={e => setFilters(f => ({ ...f, channel: e.target.value }))}
          className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm"
        >
          <option value="">Tous canaux</option>
          <option value="EMAIL">Email</option>
          <option value="WHATSAPP">WhatsApp</option>
          <option value="NONE">Non joignable</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="p-8 text-center text-gray-500">Chargement...</div>
      ) : recipients.length === 0 ? (
        <div className="p-8 text-center text-gray-500">Aucun destinataire</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <th className="text-left py-3 px-4 font-medium text-gray-500">
                  {selectionEnabled ? (
                    <button
                      type="button"
                      onClick={() => handleToggleAll(!recipients.every(r => r.is_selected))}
                      disabled={bulkUpdating}
                      className="text-xs px-2 py-1 rounded border border-gray-300 bg-white hover:bg-gray-50"
                    >
                      {recipients.every(r => r.is_selected) ? 'Tout décocher' : 'Tout cocher'}
                    </button>
                  ) : (
                    <span className="text-xs text-gray-400">Sélection</span>
                  )}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Client</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Contact</th>
                <th className="text-center py-3 px-4 font-medium text-gray-500">Canal</th>
                <th className="text-center py-3 px-4 font-medium text-gray-500">Statut</th>
                <th className="text-right py-3 px-4 font-medium text-gray-500">Montant</th>
                <th className="text-right py-3 px-4 font-medium text-gray-500">Retard</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Envoyé</th>
              </tr>
            </thead>
            <tbody>
              {recipients.map(r => {
                const statusBadge = STATUS_BADGES[r.status] || STATUS_BADGES.PENDING;
                const isExpanded = expandedId === r.id;

                return (
                  <>
                    <tr
                      key={r.id}
                      className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
                      onClick={() => setExpandedId(isExpanded ? null : r.id)}
                    >
                      <td className="py-3 px-4">
                        <input
                          type="checkbox"
                          checked={!!r.is_selected}
                          disabled={!selectionEnabled}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleToggleRecipient(r.id, e.target.checked);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4"
                        />
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {r.customer_name_full || `Client #${r.customer_id}`}
                        </div>
                        <div className="text-xs text-gray-500">{r.reference_type}</div>
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                        {r.customer_email || r.customer_phone || '-'}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {r.resolved_channel === 'EMAIL' && <span title="Email">📧</span>}
                        {r.resolved_channel === 'WHATSAPP' && <span title="WhatsApp">📱</span>}
                        {r.resolved_channel === 'NONE' && <span title="Non joignable" className="text-orange-500">⚠</span>}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                          {statusBadge.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-medium">
                        {parseFloat(r.amount || 0).toFixed(2)} €
                      </td>
                      <td className="py-3 px-4 text-right">
                        {r.days_overdue}j
                      </td>
                      <td className="py-3 px-4 text-gray-500">
                        {formatDate(r.sent_at)}
                      </td>
                    </tr>
                    {/* Expanded Details */}
                    {isExpanded && (
                      <tr className="bg-gray-50 dark:bg-gray-800/30">
                        <td colSpan="8" className="px-4 py-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Rendered Subject/Body */}
                            {r.rendered_subject && (
                              <div>
                                <div className="text-xs font-medium text-gray-500 mb-1">Sujet Email</div>
                                <div className="text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 p-2 rounded border">
                                  {r.rendered_subject}
                                </div>
                              </div>
                            )}
                            {r.rendered_body && (
                              <div>
                                <div className="text-xs font-medium text-gray-500 mb-1">Corps Email</div>
                                <div className="text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 p-2 rounded border max-h-32 overflow-y-auto">
                                  {r.rendered_body?.substring(0, 500)}...
                                </div>
                              </div>
                            )}
                            {r.rendered_whatsapp && (
                              <div>
                                <div className="text-xs font-medium text-gray-500 mb-1">Message WhatsApp</div>
                                <div className="text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 p-2 rounded border">
                                  {r.rendered_whatsapp}
                                </div>
                              </div>
                            )}
                            {/* Error Message */}
                            {r.error_message && (
                              <div className="md:col-span-2">
                                <div className="text-xs font-medium text-red-500 mb-1">Erreur</div>
                                <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                                  {r.error_message}
                                </div>
                              </div>
                            )}
                            {/* Open tracking */}
                            {r.opened_at && (
                              <div>
                                <div className="text-xs font-medium text-gray-500 mb-1">Tracking</div>
                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                                  👁 Ouvert le {formatDate(r.opened_at)}{r.open_count > 1 ? ` (${r.open_count}×)` : ''}
                                </span>
                              </div>
                            )}
                            {/* Internal note */}
                            <div className="md:col-span-2">
                              <div className="text-xs font-medium text-gray-500 mb-1">Note interne</div>
                              <div className="flex gap-2 items-start">
                                <textarea
                                  rows={2}
                                  value={notes[r.id] ?? (r.internal_note || '')}
                                  onChange={e => setNotes(n => ({ ...n, [r.id]: e.target.value }))}
                                  placeholder="Ajouter une note..."
                                  className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded bg-white resize-none"
                                  onClick={e => e.stopPropagation()}
                                />
                                <button
                                  onClick={e => { e.stopPropagation(); handleSaveNote(r.id); }}
                                  disabled={savingNote[r.id]}
                                  className="px-2 py-1 text-xs bg-slate-700 text-white rounded hover:bg-slate-900 disabled:opacity-50 whitespace-nowrap"
                                >
                                  {savingNote[r.id] ? '...' : '✓ Sauver'}
                                </button>
                              </div>
                            </div>
                            {/* Template Variables */}
                            {r.template_variables && (
                              <div className="md:col-span-2">
                                <div className="text-xs font-medium text-gray-500 mb-1">Variables</div>
                                <div className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 p-2 rounded border font-mono">
                                  {JSON.stringify(r.template_variables, null, 2)}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <span className="text-sm text-gray-500">{pagination.total} destinataire(s)</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              disabled={pagination.page === 1}
              className="px-3 py-1 border rounded text-sm disabled:opacity-50"
            >
              Préc.
            </button>
            <span className="px-3 py-1 text-sm">
              {pagination.page} / {totalPages}
            </span>
            <button
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              disabled={pagination.page >= totalPages}
              className="px-3 py-1 border rounded text-sm disabled:opacity-50"
            >
              Suiv.
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
